import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Loader2, Trash2, Sun, Sunset, CalendarClock, Phone } from "lucide-react";
import { ApexDatePicker, toIsoDate } from "../ApexDatePicker";
import api from "../../lib/api";

const PART_OPTIONS = [
  { id: "morning", label: "Dopoledne", icon: Sun },
  { id: "afternoon", label: "Odpoledne", icon: Sunset },
  { id: "full", label: "Celý den", icon: CalendarClock },
];

export const MAX_PREFERRED_DAYS = 5;

/**
 * Replaces the old "exact date + exact time slot" calendar. Customers no
 * longer pick a binding time slot themselves — they propose up to 5
 * candidate days, each tagged dopoledne/odpoledne/celý den, and staff calls
 * them back to negotiate + lock in the real, binding appointment via
 * "Sjednaný termín" in the admin dashboard.
 */
export default function StepPreferredDays({ preferredDays, setPreferredDays, showErrors }) {
  const [availability, setAvailability] = useState({}); // { "YYYY-MM-DD": {morningAvailable, afternoonAvailable} }
  const [loading, setLoading] = useState(true);
  const [pickerDate, setPickerDate] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/bookings/day-availability", { params: { days: 120 } })
      .then((r) => {
        if (!active) return;
        const map = {};
        (r.data?.days || []).forEach((d) => { map[d.date] = d; });
        setAvailability(map);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const partAvailability = (iso) => availability[iso] || { morningAvailable: true, afternoonAvailable: true };
  const isFullyBlocked = (date) => {
    if (loading) return false; // don't optimistically block before data arrives
    const av = partAvailability(toIsoDate(date));
    return !av.morningAvailable && !av.afternoonAvailable;
  };

  const addDay = (date) => {
    const iso = toIsoDate(date);
    if (preferredDays.some((d) => d.date === iso) || preferredDays.length >= MAX_PREFERRED_DAYS) return;
    const av = partAvailability(iso);
    const defaultPart = av.morningAvailable && av.afternoonAvailable ? "full" : av.morningAvailable ? "morning" : "afternoon";
    setPreferredDays([...preferredDays, { date: iso, part: defaultPart }]);
  };
  const removeDay = (iso) => setPreferredDays(preferredDays.filter((d) => d.date !== iso));
  const setPart = (iso, part) => setPreferredDays(preferredDays.map((d) => (d.date === iso ? { ...d, part } : d)));

  const today = new Date();
  const minDate = new Date(today); minDate.setDate(minDate.getDate() + 1);
  const maxDate = new Date(today); maxDate.setDate(maxDate.getDate() + 120);
  const atMax = preferredDays.length >= MAX_PREFERRED_DAYS;

  return (
    <div>
      <h3 className="font-display text-xl text-apex-cream font-bold mb-1">Kdy vám to vyhovuje?</h3>
      <p className="text-sm text-apex-cream/60 mb-5">
        Vyberte až {MAX_PREFERRED_DAYS} dnů, které vám vyhovují, a u každého zvolte dopoledne, odpoledne, nebo celý den.
      </p>

      <div className="flex items-center gap-3 mb-5">
        <ApexDatePicker
          value={pickerDate}
          onSelect={(d) => { addDay(d); setPickerDate(null); }}
          fromDate={minDate}
          toDate={maxDate}
          disabled={atMax ? () => true : isFullyBlocked}
          placeholder={atMax ? `Vybráno max. ${MAX_PREFERRED_DAYS} dnů` : "+ Přidat den"}
          testId="preferred-day-picker"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-apex-gold" />}
      </div>

      <div className="space-y-2.5" data-testid="preferred-days-list">
        <AnimatePresence>
          {preferredDays.map((pd) => {
            const av = partAvailability(pd.date);
            const dt = new Date(`${pd.date}T00:00:00`);
            return (
              <motion.div
                key={pd.date}
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}
                className="rounded-xl border border-apex-line bg-apex-card p-4"
                data-testid={`preferred-day-${pd.date}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2 text-apex-cream font-semibold text-sm">
                    <CalendarDays className="w-4 h-4 text-apex-gold flex-shrink-0" />
                    {dt.toLocaleDateString("cs-CZ", { weekday: "short", day: "numeric", month: "numeric", year: "numeric" })}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDay(pd.date)}
                    className="text-apex-cream/50 hover:text-red-400 transition-colors"
                    data-testid={`preferred-day-${pd.date}-remove`}
                    aria-label="Odstranit den"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  {PART_OPTIONS.map((opt) => {
                    const isDisabled =
                      opt.id === "full" ? !(av.morningAvailable && av.afternoonAvailable) :
                      opt.id === "morning" ? !av.morningAvailable : !av.afternoonAvailable;
                    const active = pd.part === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => setPart(pd.date, opt.id)}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                          active
                            ? "bg-apex-gold text-apex-bg border-apex-gold"
                            : isDisabled
                              ? "opacity-30 cursor-not-allowed border-apex-line text-apex-cream/40"
                              : "border-apex-line text-apex-cream/75 hover:border-apex-gold/50"
                        }`}
                        data-testid={`preferred-day-${pd.date}-part-${opt.id}`}
                      >
                        <opt.icon className="w-3.5 h-3.5" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {preferredDays.length === 0 && (
          <div className="rounded-xl border border-dashed border-apex-line px-4 py-6 text-center text-sm text-apex-cream/45" data-testid="preferred-days-empty">
            Zatím jste nevybrali žádný den.
          </div>
        )}
      </div>

      {showErrors && preferredDays.length === 0 && (
        <p className="mt-2 text-xs text-red-400" data-testid="preferred-days-error">Vyberte prosím alespoň 1 den.</p>
      )}

      <div className="mt-6 rounded-xl bg-apex-gold/10 border border-apex-gold/25 px-4 py-3.5 text-sm text-apex-cream/80 flex items-start gap-2.5" data-testid="preferred-days-note">
        <Phone className="w-4 h-4 text-apex-gold flex-shrink-0 mt-0.5" />
        <span>Po odeslání žádosti vás budeme kontaktovat a společně domluvíme konkrétní den a čas, který bude závazný. Služby probíhají v rozmezí od 8:00 do 22:00.</span>
      </div>
    </div>
  );
}
